import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { detectPlaybackProvider, controlSrc, formatClock, type PlayerEvent } from '@mlm/shared';
import {
  VideoPlayer,
  type PlaybackAdapter,
  type PlayerControls,
  type PlayerSnapshot,
  type PlayerSource,
} from '@mlm/ui';
import type { LessonSource } from '../lib/api';
import { useLessonCaptions } from './use-lesson-captions';
import { decideCompletion } from '@mlm/shared';
import {
  clampSeek,
  markSnap,
  observePlayback,
  seedSeekState,
  seekLimit,
  type SeekState,
} from '@mlm/shared';
import { resolveResumePosition, shouldAnnounceResume } from '@mlm/shared';
import { usePlayerPrefs } from '../lib/player/prefs-store';

/** Re-presign this long before the URL dies; a seek past an expired URL 403s. */
const REFRESH_LEAD_SECONDS = 300;
const REFRESH_BACKOFF_MS = [15_000, 30_000];
/** An error this soon after a good refresh means the object itself is broken. */
const REFRESH_QUIET_MS = 30_000;

export type AdapterEventMeta = {
  resumed: boolean;
  refreshing: boolean;
  /** Furthest second watched, so the heartbeat can persist it. */
  maxWatched?: number;
};

/**
 * Host for one lesson's playback.
 *
 * The chrome, the adapters and the postMessage protocol all live in
 * `@mlm/ui` / `@mlm/shared`; what stays here is the per-lesson STATE MACHINE,
 * because every hard-won rule about ordering is in it:
 *
 *  - resume-at-position is applied exactly ONCE per lesson, and only after the
 *    backend says it is ready — a `timeupdate` at 0:00 before that would
 *    overwrite the very position we are restoring;
 *  - a presigned-URL refresh restores the LIVE playhead, never the (now stale)
 *    server position, and never re-arms the resume;
 *  - `ended` fires the completion once per lesson, not once per source, so a
 *    refresh at 91 % cannot re-fire it;
 *  - every ref is keyed on the LESSON, never on the `src`, because the src is
 *    what changes underneath a refresh.
 *
 * All of it is reported upward through `onAdapterEvent`'s `meta`, so the
 * heartbeat can hold its fire while any of it is in flight.
 */
export function LessonPlayer({
  source,
  title,
  lessonId,
  chrome = 'native',
  resumeAt = null,
  completedAt = null,
  autoplayIntent = true,
  guest = false,
  onEnded,
  onNeedsRefresh,
  onAdapter,
  onAdapterEvent,
  onBookmark,
  overlay,
  notice,
  seekRestricted = false,
  completeArticleOnRead = false,
  watchedMax = 0,
  fullyWatchedAt = null,
}: {
  source: LessonSource;
  title: string;
  lessonId: string;
  chrome?: 'native' | 'embed-strip' | 'none';
  resumeAt?: number | null;
  completedAt?: string | null;
  autoplayIntent?: boolean;
  guest?: boolean;
  onEnded: () => void;
  /** Ask the page for a fresh source; resolves null when it could not. */
  onNeedsRefresh?: () => Promise<LessonSource | null>;
  onAdapter?: (adapter: PlaybackAdapter | null, controls: PlayerControls | null) => void;
  onAdapterEvent?: (event: PlayerEvent, snapshot: PlayerSnapshot, meta: AdapterEventMeta) => void;
  onBookmark?: () => void;
  overlay?: ReactNode;
  notice?: string | null;
  /** `lms_seek_restriction`: refuse a seek past what has been watched. */
  seekRestricted?: boolean;
  /** `lms_manual_completion` off: an article fires `onEnded` once its end is on screen. */
  completeArticleOnRead?: boolean;
  /** Server-side watch mark for this lesson, seeded into the policy. */
  watchedMax?: number;
  /** Server-side "reached the end once": unrestricted from then on. */
  fullyWatchedAt?: string | null;
}) {
  const rawSrc = source.kind === 'article' ? '' : source.src;
  const expiresInSeconds = source.kind === 'article' ? undefined : source.expiresInSeconds;
  const captionTracks = useLessonCaptions(source.kind === 'video' ? source.captions : undefined);

  const prefsRate = usePlayerPrefs((s) => s.rate);
  const prefsCaptionsLang = usePlayerPrefs((s) => s.captionsLang);
  const setPrefsRate = usePlayerPrefs((s) => s.setRate);
  const setPrefsCaptionsLang = usePlayerPrefs((s) => s.setCaptionsLang);

  const adapterRef = useRef<PlaybackAdapter | null>(null);
  const [adapter, setAdapter] = useState<PlaybackAdapter | null>(null);
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stalled, setStalled] = useState(false);

  // Per-LESSON guards. Reset only when the lesson changes — a src swap is a
  // refresh of the same lesson and must not re-arm any of them.
  const resumeAppliedRef = useRef(false);
  const pendingResumeRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const refreshFailuresRef = useRef(0);
  const lastRefreshOkRef = useRef(0);
  const autoplayTriedRef = useRef(false);
  /**
   * Seek policy state. Seeded SYNCHRONOUSLY from props during render (not in an
   * effect) because the resume seek can be issued from the very first adapter
   * event, which runs before effects for that lesson have flushed — a resume
   * against an unseeded state would clamp to 0 and silently restart the lesson.
   * Seeded only UPWARD: a stale server value must never lower a live mark.
   */
  const seekStateRef = useRef<SeekState>(
    seedSeekState({ maxPositionSeconds: watchedMax, fullyWatchedAt }),
  );
  const seekSeededForRef = useRef(lessonId);
  if (seekSeededForRef.current !== lessonId) {
    seekSeededForRef.current = lessonId;
    seekStateRef.current = seedSeekState({ maxPositionSeconds: watchedMax, fullyWatchedAt });
  } else {
    const fromServer = seedSeekState({ maxPositionSeconds: watchedMax, fullyWatchedAt });
    if (fromServer.maxWatched > seekStateRef.current.maxWatched) {
      seekStateRef.current = { ...seekStateRef.current, maxWatched: fromServer.maxWatched };
    }
    if (fromServer.fullyWatched) seekStateRef.current.fullyWatched = true;
  }
  const restrictedRef = useRef(seekRestricted);
  restrictedRef.current = seekRestricted && !guest;
  const controlsRef = useRef<PlayerControls | null>(null);

  useEffect(() => {
    resumeAppliedRef.current = false;
    pendingResumeRef.current = null;
    firedRef.current = false;
    refreshInFlightRef.current = false;
    refreshFailuresRef.current = 0;
    lastRefreshOkRef.current = 0;
    autoplayTriedRef.current = false;
    setResumeNotice(null);
    setStalled(false);
  }, [lessonId]);

  // Mirrored so the adapter subscription never re-binds mid-lesson.
  const resumeAtRef = useRef(resumeAt);
  resumeAtRef.current = resumeAt;
  const completedAtRef = useRef(completedAt);
  completedAtRef.current = completedAt;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onAdapterEventRef = useRef(onAdapterEvent);
  onAdapterEventRef.current = onAdapterEvent;
  const refreshingRef = useRef(refreshing);
  refreshingRef.current = refreshing;

  const playerSource: PlayerSource | null = useMemo(() => {
    if (source.kind === 'article') return null;
    if (source.kind === 'video') {
      return {
        kind: 'video',
        src: source.src,
        expiresInSeconds: source.expiresInSeconds,
        captions: captionTracks,
      };
    }
    const provider = detectPlaybackProvider(source.src);
    return {
      kind: 'iframe',
      // enablejsapi/origin/playsinline are injected HERE, at the call site:
      // `resolveRpEmbed`'s output is shared with replicated pages and training
      // videos and is asserted verbatim by their tests.
      src: controlSrc(source.src, provider, {
        origin: typeof window === 'undefined' ? '' : window.location.origin,
        playsinline: true,
      }),
      provider,
    };
  }, [source, captionTracks]);

  const doRefresh = useCallback(async () => {
    if (!onNeedsRefresh || refreshInFlightRef.current) return;
    // A media error moments after a good refresh is the object, not the URL.
    if (lastRefreshOkRef.current && Date.now() - lastRefreshOkRef.current < REFRESH_QUIET_MS) {
      setStalled(true);
      return;
    }
    refreshInFlightRef.current = true;
    setRefreshing(true);
    const next = await onNeedsRefresh().catch(() => null);
    refreshInFlightRef.current = false;
    setRefreshing(false);
    if (next) {
      refreshFailuresRef.current = 0;
      lastRefreshOkRef.current = Date.now();
      return;
    }
    const attempt = refreshFailuresRef.current;
    refreshFailuresRef.current = attempt + 1;
    const backoff = REFRESH_BACKOFF_MS[attempt];
    // After the backoff ladder, stop retrying and let the learner decide —
    // an invisible retry loop against a dead object helps nobody.
    if (backoff == null) setStalled(true);
    else setTimeout(() => void doRefresh(), backoff);
  }, [onNeedsRefresh]);

  // Re-presign shortly BEFORE expiry. Keyed on the source identity only, so a
  // heartbeat or an unrelated page re-render can never restart the countdown
  // and leave the URL to die mid-lesson.
  useEffect(() => {
    if (source.kind !== 'video' || !expiresInSeconds || !onNeedsRefresh) return;
    const leadMs = Math.max(30, expiresInSeconds - REFRESH_LEAD_SECONDS) * 1000;
    const timer = window.setTimeout(() => void doRefresh(), leadMs);
    return () => window.clearTimeout(timer);
  }, [rawSrc, source.kind, expiresInSeconds, onNeedsRefresh, doRefresh]);

  const handleEvent = useCallback(
    (event: PlayerEvent, snapshot: PlayerSnapshot) => {
      // 1. Resume, exactly once, as soon as the backend can accept a seek.
      if (!resumeAppliedRef.current && snapshot.status === 'ready') {
        const target = resolveResumePosition({
          serverPosition: resumeAtRef.current,
          duration: snapshot.duration,
          completedAt: completedAtRef.current,
        });
        // Wait for a duration if the position needs clamping against one.
        if (snapshot.duration != null || !resumeAtRef.current) {
          resumeAppliedRef.current = true;
          if (target > 0) {
            adapterRef.current?.seek(target);
            pendingResumeRef.current = target;
            if (shouldAnnounceResume(target)) {
              setResumeNotice(`Resumed from ${formatClock(target)}`);
              window.setTimeout(() => setResumeNotice(null), 2500);
            }
          }
        }
      }

      // 2. Deferred resume: iOS drops commands issued before the first tap, so
      //    re-issue once if playback clearly started from the beginning anyway.
      const pending = pendingResumeRef.current;
      if (pending != null) {
        if (event.type === 'time' && snapshot.currentTime >= pending - 2) {
          pendingResumeRef.current = null;
        } else if (
          event.type === 'state' &&
          event.state === 'playing' &&
          snapshot.currentTime < 1
        ) {
          pendingResumeRef.current = null;
          adapterRef.current?.seek(pending);
        }
      }

      // 3. Completion — once per lesson, both backends, `ended` or 90 %.
      if (
        decideCompletion({
          ended: snapshot.state === 'ended',
          currentTime: snapshot.currentTime,
          duration: snapshot.duration,
          alreadyFired: firedRef.current,
          alreadyDone: false,
        })
      ) {
        firedRef.current = true;
        onEndedRef.current();
      }

      if (event.type === 'error' && source.kind === 'video') void doRefresh();

      // 4. Watch tracking + skip enforcement. Every `time`/`seeked` sample is
      //    folded into the policy, which advances the mark for real playback
      //    and reports a jump. The snap uses the RAW adapter: routing it back
      //    through the guarded one would clamp the snap itself.
      if (event.type === 'time' || event.type === 'seeked' || event.type === 'state') {
        const now = Date.now();
        const { state: nextState, snapTo } = observePlayback(seekStateRef.current, {
          currentTime: snapshot.currentTime,
          at: now,
          rate: snapshot.rate ?? 1,
          playing: snapshot.state === 'playing',
          ended: snapshot.state === 'ended',
        });
        seekStateRef.current = nextState;
        if (snapTo != null && restrictedRef.current && resumeAppliedRef.current) {
          seekStateRef.current = markSnap(seekStateRef.current, now);
          adapterRef.current?.seek(snapTo);
          // INLINE feedback: a toast renders outside the fullscreen subtree and
          // would be invisible exactly when the learner is most likely to try.
          controlsRef.current?.flash('Watch to here first');
        }
      }

      onAdapterEventRef.current?.(event, snapshot, {
        resumed: resumeAppliedRef.current,
        refreshing: refreshingRef.current,
        maxWatched: seekStateRef.current.maxWatched,
      });
    },
    [doRefresh, source.kind],
  );

  const onAdapterRef = useRef(onAdapter);
  onAdapterRef.current = onAdapter;
  const handleAdapter = useCallback(
    (next: PlaybackAdapter | null, controls: PlayerControls | null) => {
      adapterRef.current = next;
      controlsRef.current = controls;
      /**
       * The page gets a GUARDED adapter, so every app-initiated seek is clamped
       * in one place: the control bar, the keyboard map, note and bookmark
       * chips, up-next and the parked cross-lesson seek all call `seek()` on
       * this object. `adapterRef` keeps the raw one for the snap-back above.
       */
      const guarded: PlaybackAdapter | null = next
        ? {
            ...next,
            seek: (seconds: number) => {
              const { seconds: clamped, blocked } = clampSeek(
                seconds,
                seekStateRef.current,
                restrictedRef.current,
              );
              if (blocked) controls?.flash('Watch to here first');
              return next.seek(clamped);
            },
          }
        : null;
      setAdapter(guarded);
      onAdapterRef.current?.(guarded, controls);
    },
    [],
  );

  // Autoplay on mount, synchronously enough for the browser to allow it where
  // it can (the Capacitor WebView always can). A rejection is not an error:
  // VideoPlayer already shows the big Play glyph, and the learner taps once.
  /**
   * Article bodies are sanitized at WRITE time by the API; this is
   * defence-in-depth on render, the same arrangement the replicated page uses.
   * `target` must be allowed explicitly — DOMPurify drops it by default, and
   * the server deliberately forces `target="_blank"` on every link, so without
   * this an article link would navigate the player itself away.
   *
   * Returns null for a legacy plain-text body (no markup at all), which the
   * render below keeps in `whitespace-pre-wrap`.
   */
  const articleHtml = useMemo(() => {
    if (source.kind !== 'article') return null;
    if (!source.body.includes('<')) return null;
    return DOMPurify.sanitize(source.body, { ADD_ATTR: ['target'] });
  }, [source]);

  useEffect(() => {
    if (!adapter || !autoplayIntent || autoplayTriedRef.current) return;
    if (adapter.getSnapshot().status !== 'ready') return;
    autoplayTriedRef.current = true;
    void adapter.play().catch(() => {});
  }, [adapter, autoplayIntent]);

  // With no Mark complete button an article has no other way to finish, so it
  // completes once its end has scrolled into the reading pane: the reading
  // equivalent of the 90 % rule. A short article that fits completes on open.
  const articlePaneRef = useRef<HTMLDivElement>(null);
  const articleEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (source.kind !== 'article' || !completeArticleOnRead || guest || completedAt) return;
    const root = articlePaneRef.current;
    const end = articleEndRef.current;
    if (!root || !end) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (firedRef.current || !entries.some((e) => e.isIntersecting)) return;
        firedRef.current = true;
        onEndedRef.current();
      },
      { root },
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [source, lessonId, completeArticleOnRead, guest, completedAt]);

  if (source.kind === 'article') {
    return (
      <div ref={articlePaneRef} className="h-full w-full overflow-auto bg-background p-5">
        {articleHtml == null ? (
          // Pre-Phase-4 bodies were plain text; rendering them as HTML would
          // collapse every line break into one paragraph.
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{source.body}</div>
        ) : (
          <article
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: articleHtml }}
          />
        )}
        <div ref={articleEndRef} aria-hidden className="h-px" />
      </div>
    );
  }
  if (!playerSource) return null;

  return (
    <>
      <VideoPlayer
        source={playerSource}
        title={title}
        // Keyed on the LESSON: a mid-lesson re-presign swaps bytes without
        // tearing down the playback session or the postMessage handshake.
        protocolId={lessonId}
        chrome={chrome}
        preferences={{ rate: prefsRate, captionsLang: prefsCaptionsLang }}
        onEvent={handleEvent}
        onAdapter={handleAdapter}
        onBookmark={guest ? undefined : onBookmark}
        onRateChange={setPrefsRate}
        onCaptionsChange={setPrefsCaptionsLang}
        overlay={overlay}
        notice={notice ?? resumeNotice}
        seekLimit={seekLimit(seekStateRef.current, restrictedRef.current)}
      />
      {stalled && (
        <button
          type="button"
          onClick={() => {
            setStalled(false);
            refreshFailuresRef.current = 0;
            lastRefreshOkRef.current = 0;
            void doRefresh();
          }}
          className="absolute inset-x-0 bottom-16 mx-auto w-fit rounded-md bg-white/90 px-3 py-1.5 text-xs font-semibold text-black"
        >
          Reconnect
        </button>
      )}
    </>
  );
}
